import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import {
  Grid, Paper, Card, CardContent, Typography, Divider, Button, Tabs, Tab, Slide,
  Chip, Avatar, Box, FormControlLabel, Checkbox, List, ListItem, ListItemText, Dialog, Badge,
  Collapse, IconButton, Menu, MenuItem, TextField, FormControl, InputLabel, Select,
  DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AssignmentIcon from '@mui/icons-material/Assignment';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import BlockIcon from '@mui/icons-material/Block';
import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import api, { enhancedApi } from './api';
import CommunicationTab from './components/CommunicationTab';
import Billing from './components/Billing';
import FilesTab from './components/FilesTab';
import TaskForm from './components/TaskForm';
import NotesTab from './components/NotesTab';
import EnhancedWorkflowManager from './components/EnhancedWorkflowManager';
import ClientPortalInvitationDialog from './components/ClientPortalInvitationDialog';
import ClientPortalStatus from './components/ClientPortalStatus';
import EventsTab from './components/EventsTab';
import TimeEntriesTab from './components/TimeEntriesTab';
import TimelineTab from './components/TimelineTab';
import HealthScoreTab from './components/HealthScoreTab';
import TimeTracker from './components/TimeTracker';
import PageLayout from './components/ui/PageLayout';
import ManageAssignmentsDialog from './components/ManageAssignmentsDialog';
import ClientDetailsTabLayout from './components/ClientDetailsTabLayout';
import TabContentHeader from './components/TabContentHeader';
import FeatureGate from './components/FeatureGate';
import ClientContacts, { ClientContactsSidebarMini } from './components/ClientContacts';
import ClientRequestList from './components/client-requests/ClientRequestList';
import ClientRequestDetail from './components/client-requests/ClientRequestDetail';
import CreateRequestDialog from './components/client-requests/CreateRequestDialog';

const CLIENT_INFO_TYPE_LABELS = ['Individual', 'Business', 'Trust', 'Estate', 'Partnership'];

const ClientDetails = () => {
  const location = useLocation();
  const { id } = useParams();
  const navigate = useNavigate();
  const client = location.state?.client || {};
  const fromInvoices = location.state?.fromInvoices || false;
  const fromPayments = location.state?.fromPayments || false; // Add fromPayments check

  const [tabValue, setTabValue] = useState(() => {
    const raw = location.state?.initialTab;
    let t = typeof raw === 'number' && raw >= 0 ? raw : null;
    if (t != null && t >= 4 && t <= 10) t += 1;
    if (typeof t === 'number' && t >= 0 && t <= 11) return t;
    return fromInvoices || fromPayments ? 6 : 0;
  });
  const [hideHeader, setHideHeader] = useState(false);
  const [clientFiles, setClientFiles] = useState([]);
  const [clientNotes, setClientNotes] = useState([]);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [assignedTeams, setAssignedTeams] = useState([]);
  const [loadingAssigned, setLoadingAssigned] = useState(true);
  const [error, setError] = useState('');
  const [openManageDialog, setOpenManageDialog] = useState(false);
  const [workflows, setWorkflows] = useState([]);
  const [clientTasks, setClientTasks] = useState([]);
  const [clientPortalDialogOpen, setClientPortalDialogOpen] = useState(false);
  const [clientData, setClientData] = useState(client);
  const [documentRequests, setDocumentRequests] = useState([]);
  const [clientActivity, setClientActivity] = useState([]);
  const [clientActivityLoading, setClientActivityLoading] = useState(true);
  const [showReviewed, setShowReviewed] = useState(false);
  const [highlightFileId, setHighlightFileId] = useState(null);
  const [eventsRefreshTrigger, setEventsRefreshTrigger] = useState(0);
  const [timeEntriesRefreshTrigger, setTimeEntriesRefreshTrigger] = useState(0);
  const [messagesUnreadCount, setMessagesUnreadCount] = useState(0);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [aiSuggestionsLoading, setAiSuggestionsLoading] = useState(false);
  const [aiSuggestionsError, setAiSuggestionsError] = useState('');
  const [aiApplyingId, setAiApplyingId] = useState(null);
  const [aiExpandedId, setAiExpandedId] = useState(null);
  const [aiSnackbar, setAiSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [healthScore, setHealthScore] = useState(null);
  const [quickStats, setQuickStats] = useState({ openInvoicesTotal: 0, hoursLogged: 0, lastActivity: null, lastActivityDays: null });
  const [manageMenuAnchor, setManageMenuAnchor] = useState(null);
  const [contactsRefreshKey, setContactsRefreshKey] = useState(0);
  const [openContactsAddFromEdit, setOpenContactsAddFromEdit] = useState(false);
  const [selectedClientRequestId, setSelectedClientRequestId] = useState(null);
  const [createClientRequestOpen, setCreateClientRequestOpen] = useState(false);
  const [commExternalAction, setCommExternalAction] = useState(null);
  const [avatarPreviewUrl, setAvatarPreviewUrl] = useState(null);
  const avatarInputRef = useRef(null);
  const [editClientInfoOpen, setEditClientInfoOpen] = useState(false);
  const [editClientInfoSaving, setEditClientInfoSaving] = useState(false);
  const [editClientInfoError, setEditClientInfoError] = useState('');
  const [editClientInfoForm, setEditClientInfoForm] = useState({
    first_name: '',
    last_name: '',
    display_name: '',
    email: '',
    phone: '',
    street1: '',
    city: '',
    state: '',
    zipcode: '',
    client_type_label: 'Individual',
  });

  const clientTypeLabelToPayload = (label) => (label || 'Individual').toLowerCase();

  const openEditClientInfo = () => {
    const pc = clientData?.primary_contact;
    const addr = pc?.addresses?.[0];
    setEditClientInfoError('');
    setEditClientInfoForm({
      first_name: pc?.first_name || '',
      last_name: pc?.last_name || '',
      display_name: pc?.display_name || '',
      email: pc?.emails?.[0]?.email || '',
      phone: pc?.phones?.[0]?.phone || pc?.phones?.[0]?.number || '',
      street1: addr?.street1 || '',
      city: addr?.city || '',
      state: addr?.state || '',
      zipcode: addr?.zipcode || '',
      client_type_label: (() => {
        const c = (pc?.client_type || '').toLowerCase();
        const map = {
          individual: 'Individual',
          business: 'Business',
          trust: 'Trust',
          estate: 'Estate',
          partnership: 'Partnership',
        };
        return map[c] || (clientData?.is_business_client ? 'Business' : 'Individual');
      })(),
    });
    setEditClientInfoOpen(true);
  };

  const handleSaveClientInfo = async () => {
    const f = editClientInfoForm;
    const first = (f.first_name || '').trim();
    const disp = (f.display_name || '').trim();
    if (!first && !disp) {
      setEditClientInfoError('Enter a first name or display name.');
      return;
    }
    setEditClientInfoSaving(true);
    setEditClientInfoError('');
    try {
      const { data } = await enhancedApi.patchClientProfile(id, {
        first_name: f.first_name,
        last_name: f.last_name,
        display_name: f.display_name,
        email: (f.email || '').trim(),
        phone: (f.phone || '').trim(),
        street1: f.street1,
        city: f.city,
        state: f.state,
        zipcode: f.zipcode,
        client_type: clientTypeLabelToPayload(f.client_type_label),
        is_individual_client: f.client_type_label === 'Individual',
      });
      if (data) setClientData(data);
      setEditClientInfoOpen(false);
    } catch (err) {
      const d = err.response?.data;
      const pickMsg = (x) => {
        if (x == null) return '';
        if (Array.isArray(x)) return x.filter(Boolean).join(' ');
        if (typeof x === 'object') return JSON.stringify(x);
        return String(x);
      };
      let piece = '';
      if (typeof d === 'object' && d) {
        piece = pickMsg(d.email || d.phone || d.client_type || d.error || d.detail);
      } else if (typeof d === 'string') {
        piece = d;
      }
      if (!piece) piece = err.message || 'Failed to save';
      setEditClientInfoError(piece);
    } finally {
      setEditClientInfoSaving(false);
    }
  };

  const clearCommExternalAction = useCallback(() => setCommExternalAction(null), []);
  const queueCommAction = useCallback((type) => {
    setTabValue(1);
    setCommExternalAction({ type, key: Date.now() });
  }, []);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleAddTaskClick = () => {
    setShowTaskForm(true);
  };

  const handleCloseTaskForm = () => {
    setShowTaskForm(false);
    fetchClientTasks(); // Refresh tasks after create/update
  };

  const handleOptionSelect = (option) => {
    // Handle different options from sidebar
    switch (option) {
      case 'home':
        navigate('/dashboard');
        break;
      case 'folder':
        navigate('/files/clients/root');
        break;
      case 'settings':
        navigate('/settings');
        break;
      default:
        break;
    }
  };

  const fetchAssignedUsers = async () => {
    setLoadingAssigned(true);
    try {

      const response = await api.get(`/api/multi-tenant/clients/${id}/assigned-users/`);
      setAssignedUsers(response.data.assigned_users || []);
    } catch (error) {
      setError('Failed to load assigned users');
    } finally {
      setLoadingAssigned(false);
    }
  };

  const fetchAssignedTeams = async () => {
    setLoadingAssigned(true);
    try {
      const response = await api.get(`/api/multi-tenant/clients/${id}/assigned-teams/`);
      setAssignedTeams(response.data.assigned_teams || []);
    } catch (error) {
      setError('Failed to load assigned teams');
    } finally {
      setLoadingAssigned(false);
    }
  };

  const handleOpenManageDialog = () => setOpenManageDialog(true);
  const handleCloseManageDialog = () => setOpenManageDialog(false);

  const handleAssignWorkflow = async (templateId) => {
    if (!templateId || !id) return;
    try {
      await api.post('/api/multi-tenant/assign-workflow/', {
        template_id: templateId,
        client_id: id
      });
      // Refresh workflows from API
      const response = await api.get(`/api/multi-tenant/clients/${id}/workflows/`);
      const data = response.data?.results ?? response.data;
      setWorkflows(Array.isArray(data) ? data : []);
    } catch (error) {
      setError('Failed to assign workflow');
    }
  };

  const fetchWorkflows = async () => {
    if (!id) return;
    try {
      const response = await api.get(`/api/multi-tenant/clients/${id}/workflows/`);
      const data = response.data?.results ?? response.data;
      setWorkflows(Array.isArray(data) ? data : []);
    } catch (error) {
      setWorkflows([]);
    }
  };


  const fetchAISuggestions = async () => {
    if (!id) return;
    setAiSuggestionsLoading(true);
    setAiSuggestionsError('');
    try {
      const response = await enhancedApi.getAIWorkflowSuggestions(id);
      setAiSuggestions(response.data?.suggestions || []);
    } catch (err) {
      setAiSuggestionsError('Suggestions unavailable');
      setAiSuggestions([]);
    } finally {
      setAiSuggestionsLoading(false);
    }
  };

  const handleApplyAISuggestion = async (suggestionId) => {
    if (!suggestionId || !id) return;
    setAiApplyingId(suggestionId);
    try {
      await enhancedApi.applyAIWorkflowSuggestion(suggestionId, id);
      setAiSuggestions((prev) => prev.filter((s) => (s.id ?? s.template_id) !== suggestionId));
      fetchWorkflows();
      setAiSnackbar({ open: true, message: 'Workflow assigned successfully', severity: 'success' });
    } catch (err) {
      setAiSnackbar({ open: true, message: 'Failed to assign workflow', severity: 'error' });
    } finally {
      setAiApplyingId(null);
    }
  };

  const handleDismissAISuggestion = (suggestionId) => {
    setAiSuggestions((prev) => prev.filter((s) => (s.id ?? s.template_id) !== suggestionId));
  };

  const fetchClientTasks = async () => {
    try {
      const response = await api.get(`/api/multi-tenant/clients/${id}/tasks/`);
      setClientTasks(response.data.tasks || []);
    } catch (error) {
      setClientTasks([]);
    }
  };

  const fetchDocumentRequests = async () => {
    try {
      const response = await api.get(`/api/clients/${id}/document-requests/`);
      setDocumentRequests(response.data.document_requests || []);
    } catch (error) {
      setDocumentRequests([]);
    }
  };

  const handleOpenFileInFilesTab = (fileId) => {
    setTabValue(2);
    setHighlightFileId(`file-dr_${fileId}`);
  };

  const handleMarkReviewed = async (reqId) => {
    try {
      await api.patch(`/api/clients/${id}/document-requests/${reqId}/mark-reviewed/`);
      fetchDocumentRequests();
    } catch (err) {
      // Error handled silently — UI already shows stale state
    }
  };

  useEffect(() => {
    if (!id) return;

    const fetchClientData = async () => {

      try {
        const response = await api.get(`/api/multi-tenant/clients/${id}/`);
        if (response.data) {

          setClientData(response.data);
        }
      } catch (error) {
        // Error handled — clientData stays as initial value
      }
    };

    const fetchClientFiles = async () => {
      try {
        const response = await api.get(`/api/multi-tenant/clients/${id}/files/`);
        setClientFiles(response.data.files);
      } catch (error) {
        setClientFiles([]);
      }
    };

    const fetchClientNotes = async () => {
      try {
        const response = await api.get(`/api/multi-tenant/clients/${id}/notes/`);
        setClientNotes(response.data);
      } catch (error) {
        setClientNotes([]);
      }
    };

    // Always fetch client data to ensure we have the latest/complete data

    fetchClientData();

    fetchClientFiles();
    fetchClientNotes();
    fetchAssignedUsers();
    fetchAssignedTeams();
    fetchClientTasks();
    fetchDocumentRequests();

    const fetchClientActivity = async () => {
      try {
        setClientActivityLoading(true);
        const res = await api.get(`/api/multi-tenant/client-activity-feed/`, { params: { client_id: id, limit: 10 } });
        setClientActivity(res.data?.results ?? []);
      } catch {
        setClientActivity([]);
      } finally {
        setClientActivityLoading(false);
      }
    };
    fetchClientActivity();

    const fetchHealthScore = async () => {
      try {
        const res = await enhancedApi.getClientHealthScore(id);
        setHealthScore(res.data);
      } catch {
        setHealthScore(null);
      }
    };
    const fetchQuickStats = async () => {
      try {
        const [invRes, timeRes] = await Promise.all([
          api.get(`/api/multi-tenant/get-invoices/?clientId=${id}&status=Open`).catch(() => ({ data: [] })),
          api.get('/api/time-entries/summary/', { params: { client_id: id } }).catch(() => ({ data: {} })),
        ]);
        const invoices = invRes?.data?.invoices ?? invRes?.data ?? [];
        const total = Array.isArray(invoices)
          ? invoices.reduce((s, inv) => s + (parseFloat(inv.balance ?? inv.total ?? 0) || 0), 0)
          : 0;
        const hours = timeRes?.data?.total_hours ?? timeRes?.data?.hours ?? 0;
        let lastActivityDays = null;
        try {
          const tl = await enhancedApi.getClientTimeline(id, { page: 1 });
          const ts = tl.data?.items?.[0]?.timestamp;
          if (ts) {
            const diffMs = Date.now() - new Date(ts).getTime();
            lastActivityDays = Math.floor(Math.max(0, diffMs) / 86400000);
          }
        } catch {
          lastActivityDays = null;
        }
        setQuickStats((prev) => ({ ...prev, openInvoicesTotal: total, hoursLogged: hours, lastActivityDays }));
      } catch {
        setQuickStats((prev) => ({ ...prev, openInvoicesTotal: 0, hoursLogged: 0, lastActivityDays: null }));
      }
    };
    fetchHealthScore();
    fetchQuickStats();
  }, [id]);

  useEffect(() => () => {
    if (avatarPreviewUrl) URL.revokeObjectURL(avatarPreviewUrl);
  }, [avatarPreviewUrl]);

  useEffect(() => {
    if (location.pathname.endsWith('/requests')) {
      setTabValue(4);
    }
  }, [location.pathname]);

  const handleAvatarPhotoChange = (e) => {
    const f = e.target.files?.[0];
    if (!f?.type?.startsWith?.('image/')) return;
    setAvatarPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(f);
    });
    e.target.value = '';
  };

  if (!id) {
    return (
      <PageLayout onOptionSelect={handleOptionSelect}>
        <Grid container spacing={2} sx={{ p: 2, bgcolor: 'background.default', minHeight: '100vh' }}>
          <Grid item xs={12}>
            <Typography variant="h6" color="error">
              Client ID not found. Please navigate back and try again.
            </Typography>
          </Grid>
        </Grid>
      </PageLayout>
    );
  }

  // Use clientData (fetched from API) for display name - API returns display_name, or use primary_contact
  const displayName = clientData?.display_name
    || clientData?.primary_contact?.display_name
    || `${clientData?.primary_contact?.first_name || ''} ${clientData?.primary_contact?.last_name || ''}`.trim()
    || client?.display_name
    || client?.primary_contact?.display_name
    || `${client?.primary_contact?.first_name || ''} ${client?.primary_contact?.last_name || ''}`.trim()
    || (client?.first_name || client?.last_name ? `${client.first_name || ''} ${client.last_name || ''}`.trim() : null)
    || `Client #${id}`;

  const ClientInfoCard = () => (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, boxShadow: 'none' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>Client Info</Typography>
        <Tooltip title="Edit client information">
          <IconButton
            size="small"
            aria-label="Edit client information"
            onClick={openEditClientInfo}
            sx={{ color: 'text.secondary' }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box>
          <Typography variant="caption" color="text.secondary">Email</Typography>
          <Typography variant="body2">{clientData?.primary_contact?.emails?.[0]?.email || clientData?.emails?.[0]?.email || 'N/A'}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">Phone</Typography>
          <Typography variant="body2">{clientData?.primary_contact?.phones?.[0]?.number || clientData?.phones?.[0]?.number || 'N/A'}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">Address</Typography>
          <Typography variant="body2">
            {clientData?.primary_contact?.addresses?.[0]?.street1 || clientData?.addresses?.[0]?.street1 || 'N/A'}
            {clientData?.primary_contact?.addresses?.[0]?.city && `, ${clientData.primary_contact.addresses[0].city}`}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">Type</Typography>
          <Typography variant="body2">{clientType}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">Assigned</Typography>
          <Typography variant="body2">
            {loadingAssigned ? '...' : [...assignedUsers, ...assignedTeams].map((u) => u.username || u.name).join(', ') || 'None'}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );

  const QuickStatsCard = () => (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, boxShadow: 'none' }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, color: 'text.primary' }}>Quick Stats</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">Open invoices</Typography>
          <Typography variant="body2" fontWeight={600}>${(quickStats.openInvoicesTotal || 0).toLocaleString()}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">Hours logged</Typography>
          <Typography variant="body2" fontWeight={600}>{quickStats.hoursLogged ?? 0}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">Last activity</Typography>
          <Typography variant="body2" fontWeight={600}>
            {quickStats.lastActivityDays != null ? `${quickStats.lastActivityDays} day${quickStats.lastActivityDays === 1 ? '' : 's'} ago` : '—'}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );

  const ClientDetailsSection = () => (
    <Grid item xs={12} sm={4}>
      <Paper sx={{ p: 2, height: '100%', borderRadius: 2, boxShadow: 'none', border: 1, borderColor: 'divider' }}>
        <ClientPortalStatus clientId={id} onInvitationSent={() => {}} onInviteClick={() => setClientPortalDialogOpen(true)} />
        <Divider sx={{ my: 2 }} />
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.25rem', color: 'text.primary' }}>Client Info</Typography>
        <Grid container direction="column" spacing={1} sx={{ mt: 1 }}>
          <Grid item>
            <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>Work</Typography>
            <Typography variant="body2">{clientData?.primary_contact?.emails?.[0]?.email || clientData?.emails?.[0]?.email || 'N/A'}</Typography>
          </Grid>
          <Grid item>
            <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>Physical Address</Typography>
            <Typography variant="body2">{clientData?.primary_contact?.addresses?.[0]?.street1 || clientData?.addresses?.[0]?.street1 || 'N/A'}</Typography>
          </Grid>
          <Grid item>
            <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>Client</Typography>
            <Typography variant="body2">{displayName}</Typography>
          </Grid>
          <Grid item>
            <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>Client since</Typography>
            <Typography variant="body2">{clientData?.primary_contact?.client_since || clientData?.client_since || 'N/A'}</Typography>
          </Grid>
        </Grid>
        <Divider sx={{ my: 2 }} />
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.25rem', color: 'text.primary' }}>About</Typography>
        <Grid container direction="column" spacing={1} sx={{ mt: 1 }}>
          <Grid item>
            <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>EIN</Typography>
            <Typography variant="body2">N/A</Typography>
          </Grid>
          <Grid item>
            <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>Zip Code</Typography>
            <Typography variant="body2">{clientData?.primary_contact?.addresses?.[0]?.zipcode || clientData?.addresses?.[0]?.zipcode || 'N/A'}</Typography>
          </Grid>
          <Grid item>
            <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>State</Typography>
            <Typography variant="body2">{clientData?.primary_contact?.addresses?.[0]?.state || clientData?.addresses?.[0]?.state || 'N/A'}</Typography>
          </Grid>
        </Grid>
      </Paper>
    </Grid>
  );

  const activeTasks = clientTasks.filter(task => task.status !== 'completed');
  const completedTasks = clientTasks.filter(task => task.status === 'completed');

  const getHealthScoreChipColor = (score) => {
    if (score == null) return 'default';
    const s = Number(score);
    if (s >= 70) return 'success';
    if (s >= 40) return 'warning';
    return 'error';
  };

  const getTaskPriorityChipColor = (p) => {
    const x = (p || 'low').toString().toLowerCase();
    if (x === 'urgent') return 'error';
    if (x === 'high') return 'warning';
    if (x === 'medium') return 'info';
    return 'default';
  };

  const isTaskOverdue = (due) => {
    if (!due) return false;
    const d = new Date(due);
    const t = new Date();
    d.setHours(0, 0, 0, 0);
    t.setHours(0, 0, 0, 0);
    return d < t;
  };

  const getAssigneeMeta = (task) => {
    const label = task.assignee || task.assignee_name || '—';
    const u = assignedUsers.find(
      (x) => x.username === task.assignee || x.email === task.assignee
        || `${(x.first_name || '').trim()} ${(x.last_name || '').trim()}`.trim() === label,
    );
    const initials = u
      ? `${(u.first_name || '')[0] || ''}${(u.last_name || '')[0] || ''}`.toUpperCase()
        || (u.username || '?')[0].toUpperCase()
      : (label === '—' ? '?' : (label[0] || '?').toUpperCase());
    return { label, initials };
  };

  const getHealthScoreLabel = (score) => {
    if (score == null) return '—';
    const s = Number(score);
    if (s >= 70) return 'Good';
    if (s >= 40) return 'Fair';
    return 'At Risk';
  };

  const stripHtml = (str) => (str || '').replace(/<[^>]*>/g, '').trim();

  const rawClientType = (clientData?.primary_contact?.client_type || clientData?.client_type || clientData?.type || '').toLowerCase();
  const clientType = rawClientType
    ? rawClientType.charAt(0).toUpperCase() + rawClientType.slice(1)
    : 'Client';
  const initials = (displayName || '?')
    .split(/\s+/)
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || '?';

  const handleManageMenuOpen = (e) => setManageMenuAnchor(e.currentTarget);
  const handleManageMenuClose = () => setManageMenuAnchor(null);
  const handleManageEdit = () => { handleManageMenuClose(); setOpenManageDialog(true); };
  const handleManageInvite = () => { handleManageMenuClose(); setClientPortalDialogOpen(true); };
  const handleManageDeactivate = () => { handleManageMenuClose(); };
  const handleManageDelete = () => { handleManageMenuClose(); };

  const TABS = [
    { label: 'Home', index: 0 },
    { label: 'Communication', index: 1, badge: messagesUnreadCount },
    { label: 'Files', index: 2 },
    { label: 'Tasks', index: 3 },
    { label: 'Requests', index: 4 },
    { label: 'Events', index: 5 },
    { label: 'Billing', index: 6 },
    { label: 'Notes', index: 7 },
    { label: 'Time Entries', index: 8 },
    { label: 'Workflows', index: 9 },
    { label: 'Timeline', index: 10 },
    { label: 'Health Score', index: 11 },
  ];

  return (
    <PageLayout onOptionSelect={handleOptionSelect} hideGlobalSearch={tabValue === 2}>
      <Grid container spacing={2} sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      {!hideHeader && (
        <>
          <Grid item xs={12}>
            <Box
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 10,
                bgcolor: 'background.paper',
                borderBottom: 1,
                borderColor: 'divider',
                pb: 1,
              }}
            >
              {/* Header row */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  flexWrap: 'wrap',
                  gap: 2,
                  py: 1,
                  px: 0,
                }}
              >
                <input
                  ref={avatarInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleAvatarPhotoChange}
                />
                <Tooltip title="Change photo">
                  <Avatar
                    src={avatarPreviewUrl || undefined}
                    onClick={() => avatarInputRef.current?.click()}
                    sx={{
                      width: 72,
                      height: 72,
                      fontSize: '1.5rem',
                      fontWeight: 600,
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      cursor: 'pointer',
                    }}
                  >
                    {!avatarPreviewUrl ? initials : null}
                  </Avatar>
                </Tooltip>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                      <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary' }}>
                        {displayName}
                      </Typography>
                      <Chip label={clientType} size="small" variant="outlined" sx={{ fontWeight: 500 }} />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                      {healthScore != null && (
                        <Chip
                          label={`${getHealthScoreLabel(healthScore.score)} (${healthScore.score ?? '—'})`}
                          size="small"
                          color={getHealthScoreChipColor(healthScore.score)}
                          sx={{ fontWeight: 600 }}
                        />
                      )}
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleManageMenuOpen}
                        endIcon={<KeyboardArrowDownIcon />}
                        sx={{ minWidth: 'auto' }}
                      >
                        Manage
                      </Button>
                      <Menu anchorEl={manageMenuAnchor} open={!!manageMenuAnchor} onClose={handleManageMenuClose}>
                        <MenuItem onClick={handleManageEdit}>
                          <EditIcon sx={{ mr: 1, fontSize: 20 }} /> Edit
                        </MenuItem>
                        <MenuItem onClick={handleManageInvite}>
                          <PersonAddIcon sx={{ mr: 1, fontSize: 20 }} /> Invite to Portal
                        </MenuItem>
                        <MenuItem onClick={handleManageDeactivate}>
                          <BlockIcon sx={{ mr: 1, fontSize: 20 }} /> Deactivate
                        </MenuItem>
                        <MenuItem onClick={handleManageDelete} sx={{ color: 'error.main' }}>
                          <DeleteIcon sx={{ mr: 1, fontSize: 20 }} /> Delete
                        </MenuItem>
                      </Menu>
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {quickStats.lastActivityDays != null
                      ? `Last activity: ${quickStats.lastActivityDays} day${quickStats.lastActivityDays === 1 ? '' : 's'} ago`
                      : 'Last activity: —'}
                  </Typography>
                </Box>
              </Box>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                  borderBottom: 0,
                  minHeight: 40,
                  '& .MuiTab-root': { fontWeight: 600, fontSize: '0.875rem', textTransform: 'none', minHeight: 40 },
                }}
              >
                {TABS.map((t) =>
                  t.badge != null ? (
                    <Tab
                      key={t.index}
                      label={
                        <Badge badgeContent={t.badge} color="primary" invisible={t.badge === 0}>
                          <span>{t.label}</span>
                        </Badge>
                      }
                      sx={{ fontWeight: 600, fontSize: '0.875rem' }}
                    />
                  ) : (
                    <Tab key={t.index} label={t.label} sx={{ fontWeight: 600, fontSize: '0.875rem' }} />
                  )
                )}
              </Tabs>
            </Box>
          </Grid>
        </>
      )}

      <ManageAssignmentsDialog
        open={openManageDialog}
        onClose={handleCloseManageDialog}
        clientIdOrIds={id}
        onSave={() => {
          fetchAssignedUsers();
          fetchAssignedTeams();
        }}
        clientName={displayName}
      />

      <Dialog open={editClientInfoOpen} onClose={() => !editClientInfoSaving && setEditClientInfoOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Edit client information</DialogTitle>
        <DialogContent dividers>
          {editClientInfoError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setEditClientInfoError('')}>
              {editClientInfoError}
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="edit-client-type-label">Type</InputLabel>
              <Select
                labelId="edit-client-type-label"
                label="Type"
                value={editClientInfoForm.client_type_label}
                onChange={(e) => setEditClientInfoForm((prev) => ({ ...prev, client_type_label: e.target.value }))}
              >
                {CLIENT_INFO_TYPE_LABELS.map((t) => (
                  <MenuItem key={t} value={t}>{t}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="First name"
              size="small"
              fullWidth
              value={editClientInfoForm.first_name}
              onChange={(e) => setEditClientInfoForm((prev) => ({ ...prev, first_name: e.target.value }))}
            />
            <TextField
              label="Last name"
              size="small"
              fullWidth
              value={editClientInfoForm.last_name}
              onChange={(e) => setEditClientInfoForm((prev) => ({ ...prev, last_name: e.target.value }))}
            />
            <TextField
              label="Display name"
              size="small"
              fullWidth
              value={editClientInfoForm.display_name}
              onChange={(e) => setEditClientInfoForm((prev) => ({ ...prev, display_name: e.target.value }))}
            />
            <TextField
              label="Email"
              size="small"
              fullWidth
              type="email"
              value={editClientInfoForm.email}
              onChange={(e) => setEditClientInfoForm((prev) => ({ ...prev, email: e.target.value }))}
            />
            <TextField
              label="Phone"
              size="small"
              fullWidth
              value={editClientInfoForm.phone}
              onChange={(e) => setEditClientInfoForm((prev) => ({ ...prev, phone: e.target.value }))}
            />
            <TextField
              label="Street address"
              size="small"
              fullWidth
              value={editClientInfoForm.street1}
              onChange={(e) => setEditClientInfoForm((prev) => ({ ...prev, street1: e.target.value }))}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                label="City"
                size="small"
                fullWidth
                value={editClientInfoForm.city}
                onChange={(e) => setEditClientInfoForm((prev) => ({ ...prev, city: e.target.value }))}
              />
              <TextField
                label="State"
                size="small"
                sx={{ maxWidth: 100 }}
                value={editClientInfoForm.state}
                onChange={(e) => setEditClientInfoForm((prev) => ({ ...prev, state: e.target.value }))}
              />
              <TextField
                label="ZIP"
                size="small"
                sx={{ maxWidth: 120 }}
                value={editClientInfoForm.zipcode}
                onChange={(e) => setEditClientInfoForm((prev) => ({ ...prev, zipcode: e.target.value }))}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, flexWrap: 'wrap', gap: 1, justifyContent: 'space-between' }}>
          <Button
            startIcon={<PersonAddIcon />}
            onClick={() => {
              setEditClientInfoOpen(false);
              setTabValue(0);
              setOpenContactsAddFromEdit(true);
              window.setTimeout(() => {
                document.getElementById('client-contacts-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }, 150);
            }}
            disabled={editClientInfoSaving || !id}
            color="secondary"
          >
            Add contact
          </Button>
          <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
            <Button onClick={() => setEditClientInfoOpen(false)} disabled={editClientInfoSaving}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSaveClientInfo} disabled={editClientInfoSaving}>
              {editClientInfoSaving ? 'Saving…' : 'Save'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <Dialog open={showTaskForm} onClose={handleCloseTaskForm} maxWidth="sm" TransitionComponent={Transition}>
        <TaskForm clientId={id} onFormSubmit={handleCloseTaskForm} onClose={handleCloseTaskForm} />
      </Dialog>

      <Grid item xs={12} sx={{ minHeight: 480, mt: 2, minWidth: 0, maxWidth: '100%' }}>
      {tabValue === 0 && (
        <Grid container spacing={3} sx={{ mt: 2 }}>
          {/* Left column ~65% */}
          <Grid item xs={12} lg={8}>
            <Grid container spacing={3}>
              {/* To-Do - Pending document requests */}
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, boxShadow: 'none' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      To-Do
                      {documentRequests.length > 0 && (
                        <>
                          <Chip
                            label={`${documentRequests.filter(r => r.status !== 'received' && (!r.uploaded_files || r.uploaded_files.length === 0)).length} pending`}
                            size="small"
                            color="warning"
                            variant="outlined"
                          />
                          <Chip
                            label={`${documentRequests.filter(r => r.status === 'received' || (r.uploaded_files && r.uploaded_files.length > 0)).length} uploaded`}
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                          <Chip
                            label={`${documentRequests.filter(r => r.admin_reviewed_at).length} reviewed`}
                            size="small"
                            color="default"
                            variant="outlined"
                          />
                        </>
                      )}
                    </Box>
                    <Button color="primary" size="small" onClick={() => setTabValue(2)}>
                      View all Files
                    </Button>
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  {documentRequests.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 3 }}>
                      <DescriptionOutlinedIcon sx={{ fontSize: 48, color: 'action.disabled', display: 'block', mx: 'auto', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        No pending requests
                      </Typography>
                      <Button variant="outlined" size="small" startIcon={<DescriptionOutlinedIcon />} onClick={() => queueCommAction('request_docs')}>
                        Request Documents
                      </Button>
                    </Box>
                  ) : (
                    <>
                      {documentRequests.some(r => r.admin_reviewed_at) && (
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={showReviewed}
                              onChange={(e) => setShowReviewed(e.target.checked)}
                              size="small"
                            />
                          }
                          label="Show reviewed"
                          sx={{ mb: 1 }}
                        />
                      )}
                      <List dense>
                        {documentRequests
                          .filter((req) => showReviewed || !req.admin_reviewed_at)
                          .map((req) => {
                            const isUploaded = req.status === 'received' || (req.uploaded_files && req.uploaded_files.length > 0);
                            const isReviewed = !!req.admin_reviewed_at;
                            return (
                              <ListItem key={req.id} sx={{ flexDirection: 'column', alignItems: 'stretch', borderBottom: 1, borderColor: 'divider', py: 1.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                  {isUploaded ? (
                                    <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
                                  ) : (
                                    <PendingIcon sx={{ color: 'warning.main', fontSize: 20 }} />
                                  )}
                                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    {req.document_type}
                                  </Typography>
                                  <Chip
                                    label={isUploaded ? 'Uploaded' : 'Pending'}
                                    size="small"
                                    color={isUploaded ? 'success' : 'warning'}
                                    variant="outlined"
                                  />
                                  {isReviewed && (
                                    <Chip label="Reviewed" size="small" color="default" variant="outlined" />
                                  )}
                                  <Typography variant="caption" color="text.secondary">
                                    Due: {req.due_date ? new Date(req.due_date).toLocaleDateString() : 'N/A'}
                                  </Typography>
                                  {isUploaded && !isReviewed && (
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={() => handleMarkReviewed(req.id)}
                                      sx={{ ml: 'auto' }}
                                    >
                                      Mark as reviewed
                                    </Button>
                                  )}
                                </Box>
                                {req.description && (
                                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, ml: 3.5 }}>
                                    {(() => {
                                      const d = stripHtml(req.description);
                                      return d.length > 100 ? d.substring(0, 100) + '...' : d;
                                    })()}
                                  </Typography>
                                )}
                                {isUploaded && req.uploaded_files && req.uploaded_files.length > 0 && (
                                  <Box sx={{ mt: 1, ml: 3.5, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                                    {req.uploaded_files.map((f) => (
                                      <Button
                                        key={f.id}
                                        size="small"
                                        variant="text"
                                        onClick={() => handleOpenFileInFilesTab(f.id)}
                                        sx={{ textTransform: 'none', minWidth: 'auto', p: 0 }}
                                      >
                                        {f.file_name}
                                      </Button>
                                    ))}
                                  </Box>
                                )}
                              </ListItem>
                            );
                          })}
                      </List>
                    </>
                  )}
                </Paper>
              </Grid>
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, boxShadow: 'none' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.25rem', display: 'flex', justifyContent: 'space-between', mb: 1, color: 'text.primary' }}>
                    Tasks
                    <Button color="primary" size="small" startIcon={<AddIcon />} onClick={handleAddTaskClick}>
                      Add a task
                    </Button>
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  {!showTaskForm && (
                    clientTasks.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 3 }}>
                        <AssignmentIcon sx={{ fontSize: 48, color: 'action.disabled', display: 'block', mx: 'auto', mb: 1 }} />
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          No tasks yet
                        </Typography>
                        <Button color="primary" size="small" startIcon={<AddIcon />} onClick={handleAddTaskClick}>
                          Add task
                        </Button>
                      </Box>
                    ) : (
                      <List dense>
                        {activeTasks.map((task) => {
                          const { label: assigneeLabel, initials: assigneeInitials } = getAssigneeMeta(task);
                          const overdue = task.status !== 'completed' && isTaskOverdue(task.due_date);
                          const pri = task.priority || 'Low';
                          const priColor = getTaskPriorityChipColor(pri);
                          return (
                            <ListItem key={task.id} sx={{ alignItems: 'flex-start' }}>
                              <Checkbox size="small" checked={false} sx={{ p: 0, mr: 1, mt: 0.5 }} disabled />
                              <ListItemText
                                primaryTypographyProps={{ component: 'div' }}
                                secondaryTypographyProps={{ component: 'div' }}
                                primary={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                    <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
                                      {task.name}
                                    </Typography>
                                    <Chip label={pri} size="small" color={priColor} variant={priColor === 'default' ? 'outlined' : 'filled'} sx={{ height: 22 }} />
                                  </Box>
                                }
                                secondary={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mt: 0.5 }}>
                                    <Typography component="span" variant="caption" color="text.secondary">Assignee:</Typography>
                                    <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem', bgcolor: 'primary.main' }}>{assigneeInitials}</Avatar>
                                    <Typography component="span" variant="caption" color="text.secondary">{assigneeLabel}</Typography>
                                    <Typography component="span" variant="caption" color="text.secondary">| Due:</Typography>
                                    <Typography component="span" variant="caption" sx={{ color: overdue ? 'error.main' : 'text.secondary' }}>
                                      {task.due_date || 'N/A'}
                                    </Typography>
                                  </Box>
                                }
                              />
                            </ListItem>
                          );
                        })}
                      </List>
                    )
                  )}
                </Paper>
              </Grid>
              {/* Recent Files */}
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, boxShadow: 'none' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.25rem', display: 'flex', justifyContent: 'space-between', mb: 1, color: 'text.primary' }}>
                    Recent Files
                    <Button color="primary" size="small" onClick={() => setTabValue(2)}>View all</Button>
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Grid container direction="column" spacing={1}>
                    {clientFiles.length > 0 ? (
                      clientFiles.slice(0, 8).map((file, index) => (
                        <Grid item key={index}>
                          <Typography component="span" variant="body2">
                            <Button
                              component="a"
                              href={file.file_url || file.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ textTransform: 'none', p: 0, minWidth: 'auto', color: 'primary.main' }}
                            >
                              {file.file_name}
                            </Button>
                            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                              {file.uploaded_at ? new Date(file.uploaded_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : ''}
                            </Typography>
                          </Typography>
                        </Grid>
                      ))
                    ) : (
                      <Grid item>
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                          No recently added files.
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              </Grid>
              <Grid item xs={12}>
                {id ? (
                  <ClientContacts
                    clientId={id}
                    refreshKey={contactsRefreshKey}
                    onChanged={() => setContactsRefreshKey((k) => k + 1)}
                    openAddSignal={openContactsAddFromEdit}
                    onOpenAddConsumed={() => setOpenContactsAddFromEdit(false)}
                  />
                ) : null}
              </Grid>
            </Grid>
          </Grid>
          {/* Right column ~35% */}
          <Grid item xs={12} lg={4}>
            <Grid container spacing={3} direction="column">
              <Grid item xs={12}>
                <ClientInfoCard />
              </Grid>
              <Grid item xs={12}>
                <QuickStatsCard />
              </Grid>
              {id ? (
                <Grid item xs={12}>
                  <ClientContactsSidebarMini
                    clientId={id}
                    scrollToId="client-contacts-section"
                    refreshKey={contactsRefreshKey}
                  />
                </Grid>
              ) : null}
              {id && (
              <Grid item>
                <FeatureGate feature="ai_features">
                <Card variant="outlined" sx={{ borderRadius: 2, boxShadow: 'none' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AutoAwesomeIcon sx={{ color: 'primary.main' }} />
                        AI Suggested Workflows
                      </Box>
                      <Button
                        color="primary"
                        size="small"
                        onClick={fetchAISuggestions}
                        disabled={aiSuggestionsLoading}
                        startIcon={aiSuggestionsLoading ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
                      >
                        {aiSuggestionsLoading ? 'Generating suggestions...' : 'Get Suggestions'}
                      </Button>
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    {aiSuggestionsError && (
                      <Alert severity="error" sx={{ mb: 1 }}>
                        Suggestions unavailable
                      </Alert>
                    )}
                    {!aiSuggestionsLoading && !aiSuggestionsError && aiSuggestions.length === 0 && (
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                        No suggestions available
                      </Typography>
                    )}
                    {aiSuggestions.length > 0 && (
                      <Grid container spacing={2}>
                        {aiSuggestions.map((suggestion) => {
                          const suggestionId = suggestion.id ?? suggestion.template_id;
                          const confidence = suggestion.confidence ?? 0;
                          const isExpanded = aiExpandedId === suggestionId;
                          return (
                            <Grid item xs={12} key={suggestionId}>
                              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                                <CardContent>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                      {suggestion.template_name || suggestion.name || `Template #${suggestionId}`}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <Chip
                                        label={`${Math.round(confidence * 100)}% match`}
                                        size="small"
                                        color={confidence > 0.8 ? 'success' : confidence >= 0.5 ? 'warning' : 'default'}
                                        variant="outlined"
                                      />
                                      <Tooltip title="Dismiss">
                                        <IconButton size="small" onClick={() => handleDismissAISuggestion(suggestionId)}>
                                          <CloseIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </Box>
                                  </Box>
                                  <Box>
                                    <Button
                                      size="small"
                                      onClick={() => setAiExpandedId(isExpanded ? null : suggestionId)}
                                      endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                      sx={{ textTransform: 'none', p: 0, minWidth: 'auto', color: 'text.secondary' }}
                                    >
                                      Why suggested
                                    </Button>
                                    <Collapse in={isExpanded}>
                                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                        {suggestion.reason || suggestion.why_suggested || 'AI-recommended based on client profile.'}
                                      </Typography>
                                    </Collapse>
                                  </Box>
                                  <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                                    <Button
                                      variant="contained"
                                      size="small"
                                      onClick={() => handleApplyAISuggestion(suggestionId)}
                                      disabled={aiApplyingId === suggestionId}
                                      startIcon={aiApplyingId === suggestionId ? <CircularProgress size={16} color="inherit" /> : null}
                                    >
                                      {aiApplyingId === suggestionId ? 'Assigning...' : 'Assign'}
                                    </Button>
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      onClick={() => handleDismissAISuggestion(suggestionId)}
                                    >
                                      Dismiss
                                    </Button>
                                  </Box>
                                </CardContent>
                              </Card>
                            </Grid>
                          );
                        })}
                      </Grid>
                    )}
                  </CardContent>
                </Card>
                </FeatureGate>
              </Grid>
              )}
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, boxShadow: 'none' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, color: 'text.primary' }}>Portal Status</Typography>
                  <ClientPortalStatus clientId={id} onInvitationSent={() => {}} onInviteClick={() => setClientPortalDialogOpen(true)} />
                </Paper>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}

      {tabValue === 1 && (
        <ClientDetailsTabLayout showClientPanel={false}>
          <TabContentHeader title="Communications" description="Emails, calls, and messages with this client" />
          <CommunicationTab
            client={clientData?.id ? clientData : client}
            onMeetingCreated={() => setEventsRefreshTrigger((t) => t + 1)}
            onUnreadChange={setMessagesUnreadCount}
            externalAction={commExternalAction}
            onExternalActionConsumed={clearCommExternalAction}
          />
        </ClientDetailsTabLayout>
      )}

      {tabValue === 2 && (
        <ClientDetailsTabLayout showClientPanel={false}>
          <FilesTab client={clientData?.id ? clientData : client} highlightFileId={highlightFileId} onHighlightConsumed={() => setHighlightFileId(null)} />
        </ClientDetailsTabLayout>
      )}

      {tabValue === 3 && (
        <ClientDetailsTabLayout showClientPanel={false}>
          <TabContentHeader
            title="Tasks"
            description="Active and completed tasks for this client"
            actions={<Button size="small" startIcon={<AddIcon />} onClick={handleAddTaskClick}>Add task</Button>}
          />
          <Grid container spacing={2}>
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, boxShadow: 'none' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.25rem', mb: 2 }}>
                    Active tasks
                  </Typography>
                  {activeTasks.length === 0 ? (
                    <Box sx={{ textAlign: 'center', my: 4 }}>
                      <Box sx={{ fontSize: 64, display: 'block', color: 'action.disabled' }}>
                        <svg width="64" height="64" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="64" height="64" rx="32" fill="currentColor" fillOpacity="0.08"/><path d="M32 16a2 2 0 0 1 2 2v2h8a2 2 0 0 1 2 2v24a2 2 0 0 1-2 2H22a2 2 0 0 1-2-2V22a2 2 0 0 1 2-2h8v-2a2 2 0 0 1 2-2Zm0 4h-8v24h20V20h-8v2a2 2 0 0 1-2 2h-2v-2a2 2 0 0 1 2-2v-2Z" fill="currentColor" fillOpacity="0.5"/></svg>
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.25rem' }}>
                        You have no active tasks
                      </Typography>
                      <Typography>
                        There are no active tasks assigned to this client.
                      </Typography>
                    </Box>
                  ) : (
                    <List>
                      {activeTasks.map(task => (
                        <ListItem key={task.id}>
                          <ListItemText
                            primary={task.name}
                            secondary={`Assignee: ${task.assignee} | Status: ${task.status} | Due: ${task.due_date || 'N/A'}`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Paper>
              </Grid>
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, boxShadow: 'none' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.25rem', mb: 2 }}>
                    Completed tasks
                  </Typography>
                  {completedTasks.length === 0 ? (
                    <Box sx={{ textAlign: 'center', my: 4 }}>
                      <Box sx={{ fontSize: 64, display: 'block', color: 'action.disabled' }}>
                        <svg width="64" height="64" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="64" height="64" rx="32" fill="currentColor" fillOpacity="0.08"/><path d="M32 16a2 2 0 0 1 2 2v2h8a2 2 0 0 1 2 2v24a2 2 0 0 1-2 2H22a2 2 0 0 1-2-2V22a2 2 0 0 1 2-2h8v-2a2 2 0 0 1 2-2Zm0 4h-8v24h20V20h-8v2a2 2 0 0 1-2 2h-2v-2a2 2 0 0 1 2-2v-2Z" fill="currentColor" fillOpacity="0.5"/></svg>
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.25rem' }}>
                        You have no completed tasks
                      </Typography>
                      <Typography>
                        There are no completed tasks assigned to this client.
                      </Typography>
                    </Box>
                  ) : (
                    <List>
                      {completedTasks.map(task => (
                        <ListItem key={task.id}>
                          <ListItemText
                            primary={task.name}
                            secondary={`Assignee: ${task.assignee} | Status: ${task.status} | Due: ${task.due_date || 'N/A'}`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Paper>
              </Grid>
            </Grid>
        </ClientDetailsTabLayout>
      )}

      {tabValue === 4 && (
        <ClientDetailsTabLayout showClientPanel={false}>
          <TabContentHeader
            title="Requests"
            description="Document and information requests sent to this client"
            actions={
              <Button size="small" startIcon={<AddIcon />} variant="contained" onClick={() => setCreateClientRequestOpen(true)}>
                New Request
              </Button>
            }
          />
          {selectedClientRequestId ? (
            <ClientRequestDetail
              requestId={selectedClientRequestId}
              onBack={() => setSelectedClientRequestId(null)}
            />
          ) : (
            <ClientRequestList
              clientId={Number(id)}
              onNew={() => setCreateClientRequestOpen(true)}
              onSelect={(r) => setSelectedClientRequestId(r.id)}
            />
          )}
          <CreateRequestDialog
            open={createClientRequestOpen}
            onClose={() => setCreateClientRequestOpen(false)}
            initialClientId={Number(id)}
            onCreated={() => setCreateClientRequestOpen(false)}
          />
        </ClientDetailsTabLayout>
      )}

      {tabValue === 5 && (
        <ClientDetailsTabLayout showClientPanel={false}>
          <EventsTab clientId={id} refreshTrigger={eventsRefreshTrigger} />
        </ClientDetailsTabLayout>
      )}
      {tabValue === 6 && (
        <ClientDetailsTabLayout showClientPanel={false}>
          <Billing setHideHeader={setHideHeader} clientId={id} clientName={displayName} />
        </ClientDetailsTabLayout>
      )}
      {tabValue === 7 && (
        <ClientDetailsTabLayout showClientPanel={false}>
          <NotesTab client={clientData || client} />
        </ClientDetailsTabLayout>
      )}
      {tabValue === 8 && (
        <ClientDetailsTabLayout showClientPanel={false}>
          <TabContentHeader
            title="Time Entries"
            description="Time tracked for this client"
            actions={<TimeTracker clientId={id} onEntrySaved={() => setTimeEntriesRefreshTrigger((t) => t + 1)} />}
          />
          <TimeEntriesTab
            clientId={id}
            refreshTrigger={timeEntriesRefreshTrigger}
            defaultHourlyRate={clientData?.default_hourly_rate}
          />
        </ClientDetailsTabLayout>
      )}

      {tabValue === 9 && (
        <ClientDetailsTabLayout showClientPanel={false}>
          <TabContentHeader title="Workflows" description="Assigned workflows and progress" />
          <EnhancedWorkflowManager
            clientId={id}
            onWorkflowUpdate={fetchWorkflows}
            embedded
          />
        </ClientDetailsTabLayout>
      )}
      {tabValue === 10 && (
        <ClientDetailsTabLayout showClientPanel={false}>
          <TimelineTab client={clientData || client} />
        </ClientDetailsTabLayout>
      )}
      {tabValue === 11 && (
        <ClientDetailsTabLayout showClientPanel={false}>
          <TabContentHeader title="Health Score" description="Client health score and risk factors" />
          <HealthScoreTab clientId={id} />
        </ClientDetailsTabLayout>
      )}
      </Grid>

      {/* AI Suggestion Snackbar */}
      <Snackbar
        open={aiSnackbar.open}
        autoHideDuration={4000}
        onClose={() => setAiSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setAiSnackbar((prev) => ({ ...prev, open: false }))}
          severity={aiSnackbar.severity}
          variant="filled"
        >
          {aiSnackbar.message}
        </Alert>
      </Snackbar>

      {/* Client Portal Invitation Dialog */}
      <ClientPortalInvitationDialog
        open={clientPortalDialogOpen}
        onClose={() => setClientPortalDialogOpen(false)}
        client={clientData}
        onInvitationSent={() => {
          setClientPortalDialogOpen(false);
          // Optionally refresh the client portal status
        }}
      />
        </Grid>
    </PageLayout>
  );
};

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default ClientDetails;